import {
  ArrowRightLeft, BarChart3, BadgeCheck, Brain, Clock, Cpu, Flag, Gauge, GraduationCap, Inbox, Info, ListTodo, MessageSquareText,
  RefreshCw, Repeat, Shrink, Sparkle, Sparkles, SquarePen, TextCursorInput, ArrowDownToLine, Undo2, type LucideIcon,
} from "lucide-react";
import type { SlashCommand } from "@timibot/core";
import { SkillNames } from "./names";

export interface SlashItem { command: string; title: string; icon: LucideIcon }

/** App 里有用的内置命令，按常用程度排序（和 iOS 的 SlashCatalog 一致） */
const builtins: [string, string, LucideIcon][] = [
  ["new", "新对话", SquarePen],
  ["retry", "重新回答上一条", RefreshCw],
  ["undo", "撤回上一轮", Undo2],
  ["goal", "设定目标，它会一直做到完成", Flag],
  ["plan", "先出方案，不直接动手", ListTodo],
  ["bg", "放到后台去做，不占用当前对话", ArrowDownToLine],
  ["btw", "顺便问一句，不打断正在做的事", MessageSquareText],
  ["steer", "插一句话，调整正在做的事", ArrowRightLeft],
  ["loop", "每隔一段时间重复做一件事", Repeat],
  ["learn", "把一段做法学成技能", GraduationCap],
  ["refine", "复盘这段对话，记住经验", Brain],
  ["review", "找个帮手复查刚才的结果", BadgeCheck],
  ["title", "给这段对话起个名字", TextCursorInput],
  ["compress", "压缩对话，节省额度", Shrink],
  ["skills", "搜索、安装技能", Sparkles],
  ["cron", "管理定时任务", Clock],
  ["model", "切换模型", Cpu],
  ["usage", "查看用量和额度", Gauge],
  ["status", "查看对话状态", Info],
  ["insights", "使用统计", BarChart3],
  ["memory", "查看待确认的记忆", Inbox],
];

/** Hermes 自己的命令分类；这些分类里没被收录的命令都不给普通用户看 */
const builtinCategories = new Set(["Session", "Configuration", "Tools & Skills", "Info", "Exit"]);

/**
 * 输入框里打 “/” 时的联想：只列 App 里用得上的命令，全部中文；再加上已安装的技能。
 * 终端专用的命令（重绘屏幕、切换状态栏……）不出现。
 */
export function slashMatches(typed: string, catalog: SlashCommand[]): SlashItem[] {
  const q = typed.toLowerCase().trim();
  const known = new Set(catalog.map((c) => c.name.toLowerCase()));
  const items: SlashItem[] = builtins
    // 服务器没有这个命令就不显示（旧版本 Hermes）
    .filter(([name]) => catalog.length === 0 || known.has("/" + name))
    .map(([name, title, icon]) => ({ command: "/" + name, title, icon }));
  const builtinNames = new Set(items.map((i) => i.command));
  for (const cmd of catalog) {
    if (builtinNames.has(cmd.name) || builtinCategories.has(cmd.category)) continue;
    const name = cmd.name.slice(1);
    const title = SkillNames.title(name);
    items.push({ command: cmd.name, title: title === name ? `使用技能：${name}` : `使用技能：${title}`, icon: Sparkle });
  }
  const filtered = q === "/" ? items : items.filter((i) => i.command.toLowerCase().startsWith(q) || i.title.includes(q.slice(1)));
  return filtered.slice(0, q === "/" ? 12 : 8);
}
